import React from "react";
import { explisoftService, error as errorToast, success } from "@services";
import { Batch, TokenStatus } from "@shared";
import { useCSV } from "@hooks";

export const useAntiFake = (fetchBatches = true) => {

    const { exportCSV } = useCSV();
    const [batches, setBatches] = React.useState<Batch[]>([]);
    const [batchLoading, setBatchLoading] = React.useState(false);
    const [createTokenLoading, setCreateTokenLoading] = React.useState(false);
    const [downloadTokenLoading, setDownloadTokenLoading] = React.useState(false);
    const [validateTokenLoading, setValidateTokenLoading] = React.useState(false);

    const getAllBatches = React.useCallback(() => {
        if (!fetchBatches) {
            return;
        }
        setBatchLoading(true);
        explisoftService.antiFakeGetAllBatches().then((batches) => {
            setBatches(batches.data);
        }).catch((error) => {
            errorToast(error.message || "Error fetching batches");
        }).finally(() => {
            setBatchLoading(false);
        });
    }, [fetchBatches]);

    React.useEffect(() => {
        getAllBatches();
    }, [getAllBatches]);

    const createTokens = React.useCallback(async (count: number) => {
        setCreateTokenLoading(true);
        return explisoftService.antiFakeCreateTokens(count).then((tokenData) => {
            getAllBatches();
            success("Tokens created successfully");
            return exportCSV(tokenData.tokens, "tokens.csv");
        }).catch((error) => {
            errorToast(error.message || "Error creating tokens");
        })
            .finally(() => {
                setCreateTokenLoading(false);
            });
    }, [getAllBatches, exportCSV]);

    const downloadTokens = React.useCallback((batchId: string) => {
        setDownloadTokenLoading(true);
        return explisoftService.antiFakeDownloadTokens(batchId).then((tokenData) => {
            return exportCSV(tokenData.tokens, `tokens_${batchId}.csv`);
        }).catch((error) => {
            errorToast(error.message || "Error downloading tokens");
        })
            .finally(() => {
                setDownloadTokenLoading(false);
            });
    }, [exportCSV]);

    const isActive = React.useCallback(async (token: string) => {
        setValidateTokenLoading(true);
        return explisoftService.antiFakeIsTokenActive(token).then((data) => {
            return data.active === true ? TokenStatus.ACTIVE : TokenStatus.USED;
        }).catch((error) => {
            errorToast(error.message || "Error validating token");
            if (error.errorCode.errorCode === "INVALID_CAPTCHA") {
                return TokenStatus.CAPTCHA_FAILED;
            }
        })
            .finally(() => {
                setValidateTokenLoading(false);
            });
    }, []);

    const validate = React.useCallback(async (token: string) => {
        setValidateTokenLoading(true);
        return explisoftService.antiFakeValidateToken(token).then((data) => {
            return data;
        }).catch((error) => {
            errorToast(error.message || "Error validating token");
            if (error.errorCode.errorCode === "INVALID_CAPTCHA") {
                return TokenStatus.CAPTCHA_FAILED;
            }
        }).finally(() => {
            setValidateTokenLoading(false);
        });
    }, []);

    return {
        batches,
        batchLoading,
        createTokens,
        createTokenLoading,
        downloadTokens,
        downloadTokenLoading,
        isActive,
        validate,
        validateTokenLoading,
    }
};  